import React, { useEffect, useMemo } from "react";
import { TiCardActor } from "../../../@types";
import { LoadingIndicator, RenderWhen } from "../../../../../components";
import { SEARCH_FIELDS } from "../../../mappings";

import TiCardGeneralInfo from "./general/TiCardGeneralInfo";
import TiCardAnalyticsInfo from "./analytics/TiCardAnalyticsInfo";
import TiCardFileTypes from "./analytics/TiCardFileTypes";
import TiCardAdditionalInformation from "./additionalinformation/TiCardAdditionalInformation";
import TiCardPrevalenceGraph from "./analytics/TiCardPrevalenceGraph";
import TiCardAdditionalInformationReducer, {
  TiCardAdditionalInformationInitialState
} from "./additionalinformation/TiCardAdditionalInformationReducer";
import { AdditionalInformationType } from "./additionalinformation/@ŧypes/TiCardAdditionalInformationReducer";
import useGetTiCardData from "../hooks/useGetTiCardData";

interface Props {
  family?: string;
  inputValue?: string;
  onChangeInput?: (value: string) => void;
  noBorder?: boolean;
  fileTypesPivotsToSearch?: boolean;
  prerenderGraphImage?: boolean;
}

const TiCardContent = ({
  family,
  inputValue,
  onChangeInput,
  noBorder,
  fileTypesPivotsToSearch,
  prerenderGraphImage
}: Props) => {
  const oldSearchValue = inputValue ?? "";

  const { data, isLoading } = useGetTiCardData(family);

  const [fileTypesClicked, setFileTypesClicked] = React.useState<{
    [key: string]: boolean;
  }>({});
  const [state, dispatch] = React.useReducer(
    TiCardAdditionalInformationReducer,
    TiCardAdditionalInformationInitialState
  );

  const searchContainsFileType = useMemo(
    () =>
      oldSearchValue.includes(SEARCH_FIELDS.SAMPLETYPE) ||
      oldSearchValue.includes(SEARCH_FIELDS.FILETYPE) ||
      oldSearchValue.includes(SEARCH_FIELDS.TYPE) ||
      oldSearchValue.includes(SEARCH_FIELDS.FORMAT),
    [oldSearchValue]
  );

  const changeSearchValue = (fileTypes = fileTypesClicked) => {
    const fileTypesToAdd = Object.keys(fileTypes)
      .filter(key => fileTypes[key])
      .map(key => `"${key}"`);
    const fileTypesQuery = fileTypesToAdd.length
      ? `AND filetype:[${fileTypesToAdd.join(", ")}]`
      : "";

    const newSearchValue = `${oldSearchValue} ${fileTypesQuery}`;
    onChangeInput?.(newSearchValue);
  };

  const onFileTypeClick = (filetype: string) => {
    if (!onChangeInput || searchContainsFileType) return;

    const newState = {
      ...fileTypesClicked,
      [filetype]: !fileTypesClicked[filetype]
    };
    changeSearchValue(newState);
    setFileTypesClicked(newState);
  };

  const handleActorClick = (actor: TiCardActor) => {
    dispatch({
      type: AdditionalInformationType.Actor,
      data: actor
    });
  };

  const handleThreatNameDetailsClick = () => {
    if (!data?.rl.general) return;
    dispatch({
      type: AdditionalInformationType.Threat,
      data: data.rl.general
    });
  };

  const handleCloseDetails = () => {
    dispatch({
      type: AdditionalInformationType.Closed
    });
  };

  useEffect(() => {
    setFileTypesClicked({});
    onChangeInput?.(oldSearchValue);
  }, [family, oldSearchValue, onChangeInput]);

  return (
    <div className={"ticard-content"}>
      <RenderWhen condition={isLoading}>
        <LoadingIndicator
          style={{ margin: "5rem 0" }}
          message={"Loading..."}
          data-testid="ticard_loading_indicator"
        />
      </RenderWhen>
      <RenderWhen condition={!isLoading && !data}>
        <span className={"ticard-no-content"} data-testid="ticard_no_data">
          No available Threat Intelligence data
        </span>
      </RenderWhen>
      {data && (
        <div
          className={
            noBorder
              ? "ticard-content__container__no-border"
              : "ticard-content__container"
          }
        >
          <div className={"ticard-content__information"}>
            <TiCardGeneralInfo
              data={data}
              onActorClick={handleActorClick}
              onOpenThreatNameDetails={handleThreatNameDetailsClick}
            />
            <TiCardAnalyticsInfo analyticsData={data.rl.analytics} />
          </div>
          <div className={"ticard-content__filetypes_and_prevalence"}>
            <TiCardFileTypes
              fileTypes={data.rl.analytics.common_file_type || []}
              threatName={data.rl.general.threat_name || ""}
              onFileTypeClick={onFileTypeClick}
              fileTypesClicked={fileTypesClicked}
              disabled={searchContainsFileType}
              fileTypePivotsToSearch={fileTypesPivotsToSearch}
            />
            <TiCardPrevalenceGraph
              prevelanceData={data.rl.analytics.prevalence || []}
              prerenderGraphImage={prerenderGraphImage}
            />
          </div>

          <TiCardAdditionalInformation
            state={state}
            closeDetails={handleCloseDetails}
          />
        </div>
      )}
    </div>
  );
};

export default TiCardContent;
